import { useEffect } from "react";
import { styled } from "styled-components";

import { useGetDimensionsQuery } from "api/dimensionApi.slice";
import { FilterButton, HeaderTitle, MainLayout, SectionTitleUnderlined } from "common/components";
import { breakpoints, mediaStyles } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { activateModal } from "domains/modal/actions";
import { NoAccessSidebar } from "domains/monitor/noAccess/components/NoAccessSidebar";
import { EmptySearchResult } from "domains/search/components/EmptySearchResult";
import { InteractiveDataResults } from "domains/search/components/interactiveData/InteractiveDataResults";
import { KeyIssueResults } from "domains/search/components/keyIssues/KeyIssueResults";
import { PublicationsResults } from "domains/search/components/publications/PublicationsResults";
import { SearchFilter } from "domains/search/components/SearchFilter";
import { SearchInput } from "domains/search/components/SearchInput";
import { TopicResults } from "domains/search/components/topics/TopicResults";
import { VideosResults } from "domains/search/components/videos/VideosResult";
import { getSearchHasResults } from "domains/search/selectors";
import { useSearch } from "domains/search/useSearchVisibility";
import { getCanAccessCreate, getCanAccessMonitor } from "entities/permissions/selectors";

const StyledContent = styled.div`
  box-sizing: border-box;
  ${mediaStyles(({ spacing }) => `padding: 0 ${spacing}rem ${spacing}rem ${spacing}rem;`)};

  @media ${breakpoints.extraLarge} {
    padding: 3rem;
  }
`;

const StyledHeaderContainer = styled.div`
  width: 100%;
  margin-bottom: 1rem;

  ${mediaStyles(
    ({ spacing }) => `
      padding: 0 ${spacing}rem;
      margin-top: ${spacing}rem;
    `,
  )};
`;

const StyledSearchContainer = styled.div`
  padding: 1rem 0;
  display: flex;
  width: 100%;
`;

const StyledResultsContainer = styled.div`
  box-sizing: border-box;
  scroll-padding-top: 2rem;
`;

export const SearchPage = () => {
  const dispatch = useDispatch();
  const { searchVisibility } = useSearch();
  const accessCreate = useSelector(getCanAccessCreate);
  const accessMonitor = useSelector(getCanAccessMonitor);
  const search = useSelector((state) => state.domains.search.currentSearchTerm);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const searchHasResults = useSelector(getSearchHasResults); // excluding dimension results
  const track = useTrack();
  const translate = useTranslate();

  const getDimensionsQuery = useGetDimensionsQuery(
    { limit: 6, offset: 0, page: "search", search },
    { skip: search.length <= 0 },
  );

  const dimensionResultsExist =
    search.length <= 0 &&
    !getDimensionsQuery.isFetching &&
    getDimensionsQuery.data !== undefined &&
    getDimensionsQuery.data.dimensions.length > 0;

  const hasResults = searchHasResults || dimensionResultsExist;

  const hideMarketing = accessMonitor && accessCreate;

  const onFilterClick = () => dispatch(activateModal({ id: "SearchFilter" }));

  // eslint-disable-next-line jsx-a11y/no-autofocus
  const searchBar = <SearchInput autoFocus />;

  const sidePanel = (
    <>
      <SectionTitleUnderlined>{translate("app.search.title")}</SectionTitleUnderlined>
      {searchBar}
      <SearchFilter />
      {!hideMarketing && (
        <NoAccessSidebar
          trackPage="Search"
          text={translate(isAuthenticated ? "app.marketing.upgrade_message" : "app.user.sign_up_message")}
        />
      )}
    </>
  );

  const titleElement = (
    <StyledHeaderContainer>
      <HeaderTitle>{translate("app.search.title")}</HeaderTitle>
      <StyledSearchContainer>
        {searchBar}
        <FilterButton onClick={onFilterClick} />
      </StyledSearchContainer>
    </StyledHeaderContainer>
  );

  useEffect(() => track({ name: "Browse page", page: "Search" }), [track]);

  return (
    <MainLayout headerElement={titleElement} sidePanel={sidePanel}>
      <StyledContent>
        <StyledResultsContainer>
          {!hasResults && <EmptySearchResult areDimensionsFetching={getDimensionsQuery.isFetching} />}
          {searchVisibility.topics && <TopicResults />}
          {searchVisibility.keyIssues && <KeyIssueResults />}
          {searchVisibility.publications && <PublicationsResults />}
          {searchVisibility.videos && <VideosResults />}
          {searchVisibility.interactiveData && <InteractiveDataResults />}
        </StyledResultsContainer>
      </StyledContent>
    </MainLayout>
  );
};
